import {
  DOCUMENT_STATUS,
  getDocumentStatusLabel,
  isDocumentUploaded,
} from '../config/brandDetailsConfig'
import {
  BRAND_LIFECYCLE_STATUS,
  hasOpenPublication,
  hasPublicationFailed,
  isActive,
  isAwaitingPublish,
  isDraft,
  isExtractionPhase,
} from '../config/brandLifecycleConfig'
import {
  AWAITING_EXTRACTION_LABEL,
  NO_DOCUMENTS_TO_EXTRACT_LABEL,
  RAIL_STAGE,
} from '../config/documentRailConfig'

// Past these stages the brand has left the upload desk behind, and an
// uploaded document reads as one waiting its turn at extraction.
const DRAFT_STAGES = [
  RAIL_STAGE.REVIEW,
  RAIL_STAGE.PUBLISH,
  RAIL_STAGE.REPUBLISH,
  RAIL_STAGE.QUEUED,
]

export const listDocuments = (documentStatuses = []) =>
  documentStatuses.flatMap(({ documents = [] }) => documents)

export const areRequiredDocumentsUploaded = (documentStatuses = []) => {
  const required = documentStatuses.filter(({ required }) => Boolean(required))
  return (
    required.length > 0 &&
    required.every(({ status }) => isDocumentUploaded(status))
  )
}

// Only UPLOADED documents are still waiting for extraction.
export const countUploadedDocuments = (documentStatuses = []) =>
  listDocuments(documentStatuses).filter(
    ({ status }) => status === DOCUMENT_STATUS.UPLOADED,
  ).length

// Which button the rail offers, in the order the brand moves through them.
// brand_lifecycle_status decides all of it; only a DRAFT brand's stage still
// depends on how far its documents have got.
export const getRailStage = ({
  documentStatuses = [],
  // Sent from this page; the publication may not have been re-read yet.
  isPublicationAccepted = false,
  isReviewOpen = false,
  isRetryAvailable = false,
  lifecycleStatus = '',
  publication = null,
}) => {
  // Live: nothing left to publish, whatever this session last sent.
  if (isActive(lifecycleStatus)) return RAIL_STAGE.NONE
  // The current publication outranks the brand's own status, which can still
  // read REVIEW_COMPLETED after the brand has been sent. Once sent, it is
  // either waiting in the queue or has failed and needs sending again.
  if (hasOpenPublication(publication)) {
    return hasPublicationFailed(publication)
      ? RAIL_STAGE.REPUBLISH
      : RAIL_STAGE.QUEUED
  }
  if (isPublicationAccepted || isAwaitingPublish(lifecycleStatus)) {
    return RAIL_STAGE.QUEUED
  }
  // IN_REVIEW and REVIEW_COMPLETED both land here: the sections are on screen,
  // and the only way on from them is the publish queue.
  if (isReviewOpen) return RAIL_STAGE.PUBLISH
  if (
    isExtractionPhase(lifecycleStatus) ||
    lifecycleStatus === BRAND_LIFECYCLE_STATUS.IN_REVIEW
  ) {
    return RAIL_STAGE.REVIEW
  }
  // Extraction is a DRAFT brand's business alone. Anything
  // else — including a brand whose status has not loaded yet — gets no
  // button rather than one that would act on settled documents.
  if (!isDraft(lifecycleStatus)) return RAIL_STAGE.NONE
  if (isRetryAvailable) return RAIL_STAGE.EXTRACT
  return countUploadedDocuments(documentStatuses) > 0 &&
    areRequiredDocumentsUploaded(documentStatuses)
    ? RAIL_STAGE.EXTRACT
    : RAIL_STAGE.UPLOAD
}

// The status line under a document type. Only UPLOADED is refined: once the
// brand is past the upload desk it reads as waiting its turn at extraction.
export const getRailStatusLabel = ({ item, stage }) => {
  if (item.status === DOCUMENT_STATUS.EXTRACTION_FAILURE) {
    return NO_DOCUMENTS_TO_EXTRACT_LABEL
  }
  const label = getDocumentStatusLabel(item.status)
  return item.status === DOCUMENT_STATUS.UPLOADED &&
    DRAFT_STAGES.includes(stage)
    ? AWAITING_EXTRACTION_LABEL
    : label
}
